import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  KeyPress,
  isLetterChar,
  isDigitChar,
  isSpecialChar,
} from "@att-bit/duc.utils.key-press";
import { isAndroid, isIos, isIE } from "@att-bit/duc.utils.user-agent";

export type SelectProps = {
  /**  children (text/jsx) to be rendered in component  */
  children: React.ReactNode;
  /** Optional classname given to parent div of the select component. */
  className?: string;
  /** Default list item selected from the options list provided */
  value?: number;
  /** Decides if select appears disabled. Defaults to false. */
  disabled?: boolean;
  /** Text given to error message that appears below select if conditions are met. */
  errorMsg?: string;
  /** Text given to the label above the select dropdown. Defaults to 'Select an Option'. */
  label?: string;
  /** Decides if selection appears in a multiline format or single line */
  multiline?: boolean;
  /** Decides if select appears as a native dropdown. Defaults to false. */
  native?: boolean;
  /** Placeholder for mobile devices can be optional */
  nativePlaceHolder?: string;
  /** Decides if placeholder needs to be hidden for mobile devices. Defaults to false*/
  hideNativePlaceHolder?: boolean;
  /** Event handler for when the select component loses focus */
  onBlur?: (e: SelectState) => any;
  /** Event handler for when the select value changes, accepts 2 params, param 1 is the index of the option selected, and param2 is the value of the option selected */
  onChange?: (e: SelectState) => any;
  /** Event handler for when select button is pressed */
  onClick?: (e: SelectState) => any;
  /** Event handler for when the select component hits an error state */
  onError?: (e: SelectState) => any;
  /** Event handler for when the select button is pressed */
  onFocus?: (e: SelectState) => any;
  /** Event handler for when the select button is being hovered over */
  onHover?: (e: SelectState) => any;
  /** Event handler for when the select button is pressed */
  onMouseDown?: (e: SelectState) => any;
  /** Event handler for when the select button is released */
  onMouseUp?: (e: SelectState) => any;
  /** Event handler for keyPresses when the select dropdown is open */
  onKeyPress?: (k: React.KeyboardEvent<HTMLElement>, e: SelectState) => any;
  /** The placeholder text to show in this select when there is no value currently selected. */
  placeholder?: string;
  /** Used for unique  identification of element. */
  id: string;
};

const populateOptions = (children) => {
  const optionsArray = [];
  const displayValueArray = [];
  // istanbul ignore next
  React.Children.map(children, (child, idx) => {
    if (
      React.isValidElement(child) &&
      child.type === Select.Option &&
      child?.props["value"]
    ) {
      optionsArray.push(child?.props["value"]);
      displayValueArray.push(child?.props["displayValue"] || child?.props["value"]);
    } else if (
      React.isValidElement(child) &&
      child.type === Select.OptionGroup &&
      child?.props["children"] &&
      child?.props["children"]?.length
    ) {
      const children1 = [...child?.props["children"]];
      children1?.map((option) => {
        const value = option?.props?.value;
        optionsArray.push(value);
        displayValueArray.push(option?.props["displaValue"] || option?.props["value"]);
      });        
    }      
  });
  return [optionsArray, displayValueArray];
};

export const Select = ({
  children,
  value,
  disabled,
  errorMsg,
  label,
  multiline,
  native,
  nativePlaceHolder,
  hideNativePlaceHolder,
  onKeyPress,
  onError,
  onBlur,
  onMouseDown,
  onMouseUp,
  onChange,
  onClick,
  onFocus,
  onHover,
  placeholder,
  ...props
}: SelectProps) => {
  /* useRef */
  const containerRef = useRef<HTMLDivElement>();
  const buttonRef = useRef<HTMLButtonElement>();
  const dropdownRef = useRef<HTMLDivElement>();
  const [options, displayValues] = useMemo(() => populateOptions(children), [children]);
  const prevDisplayValuesRef = useRef(displayValues);
  /* stateful items */
  const [state, setState] = useState<SelectState>({
    altPressed: false,
    selectedIndex: value >= 0 ? value : -1,
    error: false,
    selectedValue: value >= 0 ? options[value] : placeholder || "Select",
    selectedDisplayValue: value >= 0 ? displayValues[value] : placeholder || "Select",
  });
  const [show, toggleDropDown] = useState<boolean>(false);
  const [refresh, setRefresh] = useState<boolean>(false);
  const [timer, setTimer] = useState<any>(null);
  const [searchResults, resetSearchResults] = useState<string[]>([]);
  const { altPressed, selectedIndex, error, selectedValue, selectedDisplayValue  } = state;

  const id = props["id"];
  const labelId = id.concat("-label");
  const errorLabelId = errorMsg ? id.concat("-error") : "";
  const errorClass = error || errorMsg ? "error" : "";
  const disabledClass = disabled ? "disabled" : "";
  const dropdownButtonClass = `dropdown-button`;
  //const options = populateOptions(children) || [];  
  let refOptions: any = options ? [...Array(options.length)].map((_, i) => i) : [];
  let groupIndex = -1;

  const setSearchResults = (): any => {
    const obj: object[] = [];

    options.forEach((value: string, i: number) => {
      obj.push({ index: i, value });
    });

    return obj;
  }; 

  useEffect(() => {
    if (show && !native)
      document.addEventListener("click", handleMouseUp, false);
  }, [show]);

  useEffect(() => {
    if (refresh) {
      setRefresh(false);
      setTimer(null);
      resetSearchResults(setSearchResults());
    }
  }, [refresh]);

  useEffect(() => {
    resetSearchResults(setSearchResults());
  }, []);

  useEffect(() => {
    setState({
      ...state,
      selectedValue: options[value],
      selectedIndex: value >= 0 ? value : -1,
      selectedDisplayValue: displayValues[value]
    });
  }, [value]);

  useEffect(() => {
    if (prevDisplayValuesRef?.current?.toString() !== displayValues?.toString()) {
      setState({
        altPressed: false,
        selectedIndex: value >= 0 ? value : -1,
        error: false,
        selectedValue: value >= 0 ? options[value] : placeholder || "Select",
        selectedDisplayValue: value >= 0 ? displayValues[value] : placeholder || "Select",
      });
      prevDisplayValuesRef.current = displayValues;
    }
  }, [displayValues]);

  useEffect(() => {
    if (!native && show) {
      // istanbul ignore next
      if (selectedIndex >= 0) {
      refOptions[selectedIndex]?.focus();
      } else if (selectedIndex === -1) {
        refOptions[selectedIndex+1]?.focus();
      }
    }
  }, [show, selectedIndex]);

  useEffect(() => {
    if (state.selectedIndex > -1 && state.selectedValue !== "") {
      onChange?.(state);
    }
  }, [state.selectedIndex, state.selectedValue]);

  const onFocusEvent = () => {
    onFocus && onFocus(state);
  };

  const onMouseDownEvent = () => {
    onMouseDown && onMouseDown(state);
  };

  const onMouseUpEvent = () => {
    onMouseUp && onMouseUp(state);
  };

  const onHoverEvent = () => {
    onHover && onHover(state);
  };

  const onKeyPressEvent = (k: React.KeyboardEvent<HTMLElement>) => {
    onKeyPress && onKeyPress(k, state);
  };

  const showDropdown = (): void => {
    toggleDropDown(!show);
    onClick?.(state);
  };

  const hideDropdown = (): void => {
    if (!native) {
      //istanbul ignore next
      // buttonRef?.current?.focus();
      document.removeEventListener("click", handleMouseUp, false);
    }
    resetSearchResults(setSearchResults());
    toggleDropDown(false);
    clearTimeout(timer);
  };

  const searchOptions = (e: React.KeyboardEvent<HTMLElement>) => {
    clearTimeout(timer);
    const letterChar: string | undefined = isLetterChar(e);
    const specialChar: string | undefined = isSpecialChar(e, altPressed);
    const digitChar: string | undefined = isDigitChar(e);
    const keyChar: string | undefined =
      letterChar || specialChar || digitChar || undefined;

    if (!keyChar) {
      return;
    }

    const temp: any = [];
    searchResults.forEach((item: any) => {
      const { value } = item;
      const optionChar: string = value.charAt(0).toUpperCase();
      if (optionChar.localeCompare(keyChar) === 0) {
        item.value = value.substring(1);
        temp.push(item);
      }
    });
    temp.forEach((singleItem: any, indexInner: number) => {
      temp[indexInner][`next`] = temp[indexInner + 1]
        ? temp[indexInner + 1].index
        : temp[0].index;
    });

    setState({
      ...state,
      selectedIndex:
        temp.length === 0
          ? selectedIndex
          : handleNextSelect(temp, selectedIndex),
    });
    resetSearchResults(temp);
    setTimer(setTimeout(() => setRefresh(true), 1000));
  };

  const handleNextSelect = (tempObj: any, currentPos: number): number => {
    let tempData: any = 0;
    //istanbul ignore else
    if (tempObj.length) {
      tempObj.forEach((item: any) => {
        if (item.index === selectedIndex) {
          tempData = item;
        }
      });
      tempData = searchResults[tempData.next]
        ? searchResults[tempData.next]
        : tempObj[0];
    }
    return tempData.index;
  };

  const handleOnBlur = () => {
    if (!show) onBlur && onBlur(state);
    if (native) toggleDropDown(false);

    //istanbul ignore if
    if (!show && !selectedValue && errorMsg) {
      onError && onError(state);
      setState({ ...state, error: true });
    }
  };

  //istanbul ignore next
  const handleMouseUp = (e: any) => {
    if (!containerRef.current?.contains(e.target)) {
      hideDropdown();
    }
  };

  const handleSelection = (value: string, i: number, displayValue?: string) => {
    //istanbul ignore next
    if (!native) {
      buttonRef?.current?.focus();
    }
    
    setState({
      ...state,
      selectedIndex: i,
      error: false,
      selectedValue: value,
      selectedDisplayValue: displayValue,
    });
    toggleDropDown(false);
  };

  const handleNativeSelection = (e: any) => {
    const { value, selectedIndex } = e.target;
    const updatedSelectedIndex = !hideNativePlaceHolder ? selectedIndex - 1 : selectedIndex;

    handleSelection(value, updatedSelectedIndex, value);
  };

  const keyPressedDropDown = (
    e: React.KeyboardEvent<HTMLElement>,
    selectedIndex
  ) => {
    onKeyPressEvent(e);
    if (KeyPress.tabIsPressed(e)) {
      hideDropdown();
      return;
    }
    e.preventDefault();
    if (KeyPress.downArrowIsPressed(e)) {      
      if (selectedIndex < options.length - 1) {
        setState({ ...state, selectedIndex: selectedIndex + 1 });
      }     
    } else if (KeyPress.upArrowIsPressed(e)) {
      if (selectedIndex > 0) {
        setState({ ...state, selectedIndex: selectedIndex - 1 });
      } 
    } else if (KeyPress.homeIsPressed(e)) {
      setState({ ...state, selectedIndex: 0 });
    } else if (KeyPress.endIsPressed(e)) {
      setState({ ...state, selectedIndex: options.length - 1 });
    } else if (KeyPress.pageUpIsPressed(e)) {
      setState({
        ...state,
        selectedIndex: selectedIndex - 3 >= 0 ? selectedIndex - 3 : 0,
      });
    } else if (KeyPress.pageDownIsPressed(e)) {
      setState({
        ...state,
        selectedIndex:
          selectedIndex + 3 < options.length
            ? selectedIndex + 3
            : options.length - 1,
      });
    } else if (KeyPress.escIsPressed(e)) {
      hideDropdown();
    } else if (KeyPress.enterIsPressed(e)) {
      handleSelection(options[selectedIndex], selectedIndex, displayValues[selectedIndex]);
    } else if (KeyPress.altIsPressed(e) || KeyPress.shiftIsPressed(e)) {
      if (!altPressed) {
        setState({ ...state, altPressed: true });
      }
    } else {
      searchOptions(e);
    }
  };

  const keyUpDropDown = (e: React.KeyboardEvent<HTMLElement>) => {
    //istanbul ignore else
    if (KeyPress.altIsPressed(e) || KeyPress.shiftIsPressed(e)) {
      setState({ ...state, altPressed: false });
    }
  };

  const updateGroupIndex = () => {
    return ++groupIndex;
  };

  return (
    <>
    { label && 
      <label
        className={`formfield-label label ${disabledClass} ${errorClass}`}
        htmlFor={id}
        id={labelId}
      >
        {label}
      </label>
    }
      <div
        id={id}
        style={props["style"]}
        className={`selectWrap ${errorClass} ${props["className"]}`}
        ref={containerRef}
      >
        {native ? (
          <React.Fragment>
            <select
              aria-invalid={!!errorMsg}
              className={`awd-select ${show ? "active" : ""} ${
                selectedIndex < 0 ? "select-placeholder" : ""
              }`}
              value={selectedValue}
              disabled={disabled}
              id={labelId}
              onBlur={handleOnBlur}
              onChange={handleNativeSelection}
              onFocus={onFocusEvent}
              onMouseDown={onMouseDownEvent}
              onMouseOver={onHoverEvent}
              onMouseUp={onMouseUpEvent}
            >
              {!hideNativePlaceHolder && 
                <option selected hidden data-placeholder={true} value={''}>
                  {nativePlaceHolder || "Select"}
                </option>
              }
              <NativeOptions
                selectedValue={selectedValue}
                selectedIndex={selectedIndex}
              >
                {children}
              </NativeOptions>
            </select>
            <span className="select-skin" />
          </React.Fragment>
        ) : (
          <>
            <button
              aria-label= { displayValues[selectedIndex] || placeholder || "Select"}
              {...(errorLabelId ? { 'aria-describedby': errorLabelId} : null)}
              aria-expanded={show}
              aria-invalid={error}
              aria-controls={`${id}-option-container`}
              className={dropdownButtonClass}
              disabled={disabled}
              ref={buttonRef}
              role="combobox"
              onBlur={handleOnBlur}
              onClick={show ? hideDropdown : showDropdown}
              onFocus={onFocusEvent}
              onMouseDown={onMouseDownEvent}
              onMouseOver={onHoverEvent}
              onMouseUp={onMouseUpEvent}
            >
              <span
                id={id.concat("text")}
                className={
                  selectedValue ? "option-selected labelText" : "labelText"
                }
              >
                {/* { options[selectedIndex] || placeholder || "Select"} */}
                { selectedDisplayValue  || placeholder || "Select"}
                
              </span>
            </button>
            <OptionWrapper
              hide={!show}
              selectedIndex={selectedIndex}
              onKeyDown={keyPressedDropDown}
              onKeyUp={keyUpDropDown}
              onClick={handleSelection}
              dropdownRef={dropdownRef}
              options={options}
              labelId={labelId}
              refOptions={refOptions}
              multiline={multiline}
              id={`${id}-option-container`}
              updateIndex={updateGroupIndex}
            >
              {children}
            </OptionWrapper>
          </>
        )}
      </div>
      <Error errorMsg={errorMsg} labelId={errorLabelId} />
      <style jsx>{`
          option[value=""][disabled] {
  display: none;
}
        `}</style>
    </>
  );
};

const OptionWrapper = (props) => {
  const {
    onKeyDown,
    onKeyUp,
    onClick,
    children,
    hide,
    selectedIndex,
    dropdownRef,
    options,
    refOptions,
    labelId,
    multiline,
    id,
    updateIndex,
  } = props;

  const className = `optionWrapper overflow-y-hidden box-shadow1${
    !hide ? " open" : ""
  }`;

  return (
    <div
      className={className}
      onKeyDown={(e) => {
        onKeyDown(e, selectedIndex);
      }}
      onKeyUp={(e) => {
        onKeyUp(e);
      }}
      aria-hidden={hide}
      tabIndex={hide ? -1 : 0}
      ref={dropdownRef}
      id={props.id}
      role="listbox"
    >
      <ul className="option-list" aria-labelledby={labelId}>
        {!hide &&
          React.Children.map(children, (child, idx) => {
            // istanbul ignore else
            if (
              React.isValidElement(child) &&
              (child.type === Select.OptionGroup ||
                child.type === Select.Option)
            ) {
              return React.cloneElement(child as React.ReactElement<any>, {
                index: idx,
                onClick,
                refOptions,
                selectedIndex,
                options,
                multiline,
                value: child.props["value"],
                updateIndex,
                displayValue: child.props["displayValue"] || child.props["value"],
              });
            }
          })}
      </ul>
    </div>
  );
};

/** ignored from coverage asit is not being considered despite the functionality is working */
// istanbul ignore next
const OptionGroup = (props) => {
  const {
    value: groupValue,
    selectedIndex,
    index,
    onClick,
    refOptions,
    options,
    multiline,
    children,
    updateIndex,
    displayValue
  } = props;

  return (
    <li className="optgroup-wrapper" key={`Group-Label-${index}`}>
      <span aria-hidden={true}>{displayValue || groupValue}</span>
      <ul className="optgroup">
        {React.Children.map(children, (child, idx) => {
          // istanbul ignore else
          if (React.isValidElement(child) && child.type === Select.Option) {
            return React.cloneElement(child as React.ReactElement<any>, {
              index: idx,
              selectedIndex,
              onClick,
              refOptions,
              options,
              multiline,
              value: child.props["value"],
              displayValue: child.props["displayValue"] || child.props["value"],
              updateIndex,
            });
          }
        })}
      </ul>
    </li>
  );
};
Select.OptionGroup = OptionGroup;

const Option = (props) => {
  const { selectedIndex, value, onClick, refOptions, options, multiline, updateIndex, displayValue } =
    props;
  const newIndex = updateIndex();
  const selected = selectedIndex === newIndex;
  const className = `${
    multiline ? " option-list-wrapper-custom " : ""
  }option-list-item${selected ? " has-focus" : ""}`;

  return (
    <>
      <li
        demo-posinset={newIndex + 1}
        aria-selected={selected}
        className={className}
        tabIndex={selected ? 0 : -1}
        key={newIndex}
        onClick={() => {
          onClick(value, newIndex, displayValue);
        }}
        role="option"
        ref={(node) => {
          refOptions[newIndex] = node;
        }}
        data-option-value={value}
      >
        {displayValue || value}
      </li>
      <style jsx>{`
        .option-list-wrapper-custom {
          border: none;
          color: #454b52 !important;
          cursor: pointer;
          line-height: 20px;
          min-height: 40px;
          padding: 10px 24px;
          text-indent: 0;
          white-space: normal;
          width: 100%;
        }
      `}</style>
    </>
  );
};
Select.Option = Option;

const NativeOptionGroup = (props) => {
  const { value: groupValue, selectedValue, index, children, displayValue } = props;

  return (
    <optgroup key={`Group-Label-${index}`} label={displayValue || groupValue}>
      {React.Children.map(children, (child, idx) => {
        // istanbul ignore else
        if (React.isValidElement(child) && child.type === Select.Option) {
          const value = child.props["value"];
          return (
            <option
              aria-selected={selectedValue === value}
              key={idx}
              value={value}
              label={displayValue || value}
            >
              {value}
            </option>
          );
        }
      })}
    </optgroup>
  );
};

const NativeOptions = (props) => {
  const { selectedIndex, children, selectedValue } = props;

  return React.Children.map(children, (child, idx) => {
    // istanbul ignore else
    if (React.isValidElement(child) && child.type === Select.OptionGroup) {
      return (
        <NativeOptionGroup
          selectedIndex={selectedIndex}
          selectedValue={selectedValue}
          value={child.props["value"]}
        >
          {child.props["children"]}
        </NativeOptionGroup>
      );
    } else if (React.isValidElement(child) && child.type === Select.Option) {
      return (
        <option aria-selected={selectedValue === child.props["value"]} key={idx} value={child.props["value"]}>
          {child.props["displayValue"] || child.props["value"] }
        </option>
      );
    }
  });
};

const Error = ({ errorMsg, labelId }) => {
  if (!errorMsg) return null;
  return (
    <div className="formfield-msg" id={labelId} role="status" tabIndex={-1}>
      {errorMsg}
    </div>
  );
};

Select.defaultProps = {
  disabled: false,
  errorMsg: "",
  label: "Select an Option",
  multiline: false,
  native: isAndroid() || isIos(),
  nativePlaceHolder: "",
  hideNativePlaceHolder: false,
};

interface SelectState {
  altPressed: boolean;
  selectedIndex: number;
  error: boolean;
  selectedValue: string;
  selectedDisplayValue: string;
}